<template>
  <div id="recebimentos-csv-import-modal-app">
    <wizard-modal
      :show-modal="show"
      :modal-title="t('Importar Recebimentos CSV')"
      size="large"
      :steps="wizardSteps"
      @step-change="(handleStepChange as any)"
      @finish="handleFinish"
      @cancel="closeModal"
      @close="closeModal">

      <!-- Step 0: File Upload -->
      <template #step-0>
        <box type="primary" :title="t('Upload do Arquivo')">
          <!-- Instructions -->
          <inline-message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <div style="line-height: 1.6;">
              <strong>{{ t('Instruções:') }}</strong>
              <ul style="margin: 0.5rem 0 0 0; padding-left: 1.5rem;">
                <li>{{ t('O arquivo CSV pode usar vírgula (,) ou ponto e vírgula (;) como separador') }}</li>
                <li>{{ t('O sistema identificará automaticamente valores positivos (recebimentos)') }}</li>
                <li>{{ t('Valores negativos (despesas/débitos) serão ignorados') }}</li>
                <li><strong>{{ t('Formato típico:') }}</strong> {{ t('Data, Histórico, Descrição, Valor') }}</li>
              </ul>
            </div>
          </inline-message>

          <!-- File Upload Section -->
          <box style="margin-bottom: 1.5rem; border: 2px dashed var(--surface-border); background: var(--surface-50);">
            <div style="text-align: center; padding: 1rem;">
              <i class="pi pi-cloud-upload" style="font-size: 3rem; color: var(--primary-color); margin-bottom: 1rem;"></i>
              <div class="form-group" style="margin-bottom: 0;">
                <label style="display: block; margin-bottom: 0.5rem; font-weight: 600;">
                  {{ t('Selecionar Arquivo CSV') }}
                </label>
                <file-model
                  data-testid="recebimento-csv-file"
                  :value="csvFile"
                  @input="handleFileUpload">
                </file-model>
              </div>
            </div>
          </box>

          <!-- Upload Progress -->
          <div v-show="uploadProgress > 0 && uploadProgress < 100" style="margin-bottom: 1.5rem;">
            <progress-bar :value="uploadProgress" :showValue="true"></progress-bar>
          </div>

          <!-- CSV Preview -->
          <div v-show="csvPreview.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
              <h5 style="margin: 0; display: flex; align-items: center; gap: 0.5rem;">
                <i class="pi pi-table"></i>
                {{ t('Preview dos Dados') }}
              </h5>
              <div style="display: flex; gap: 0.5rem;">
                <chip :label="csvHeaders.length + ' ' + t('colunas')" icon="pi pi-list" />
                <chip :label="(csvPreview.length) + ' ' + t('linhas')" icon="pi pi-bars" />
              </div>
            </div>

            <div class="table-responsive" style="border: 1px solid var(--surface-border); border-radius: 6px; overflow: hidden;">
              <table class="table table-sm table-bordered" style="margin-bottom: 0;">
                <thead style="background: var(--surface-100);">
                  <tr>
                    <th v-for="header in csvHeaders" :key="header" style="font-weight: 600; padding: 0.75rem;">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in csvPreview.slice(0, 5)" :key="index">
                    <td v-for="(cell, cellIndex) in row" :key="cellIndex" style="padding: 0.5rem;">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style="margin-top: 0.5rem; text-align: center;">
              <small class="text-muted">
                <i class="pi pi-info-circle"></i>
                {{ t('Mostrando as primeiras 5 linhas de') }} {{ csvPreview.length }} {{ t('registros encontrados') }}
              </small>
            </div>
          </div>
        </box>
      </template>

      <!-- Step 1: Field Mapping -->
      <template #step-1>
        <box type="primary" :title="t('Mapeamento de Campos')">
          <!-- Instructions -->
          <inline-message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <div>
              {{ t('Mapeie as colunas do CSV para os campos do recebimento.') }}
              <strong>{{ t('Campos marcados com') }} <span class="text-danger">*</span> {{ t('são obrigatórios.') }}</strong>
            </div>
          </inline-message>

          <!-- Required Fields -->
          <panel :header="t('Campos Obrigatórios')" :toggleable="false" style="margin-bottom: 1.5rem;">
            <div class="p-fluid">
              <!-- Valor -->
              <div class="field" style="margin-bottom: 1.5rem;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-dollar" style="color: var(--primary-color);"></i>
                  {{ t('Valor') }}
                  <chip label="*" severity="danger" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  data-testid="recebimento-csv-mapping-valor"
                  elem-id="recebimentos_csv_field_mapping_valor"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.valor">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Coluna com o valor monetário (valores positivos = recebimentos)') }}
                </small>
              </div>

              <!-- Data -->
              <div class="field" style="margin-bottom: 0;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-calendar" style="color: var(--primary-color);"></i>
                  {{ t('Data') }}
                  <chip label="*" severity="danger" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  data-testid="recebimento-csv-mapping-data"
                  elem-id="csv_field_mapping_data"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.data">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Coluna com a data do lançamento') }}
                </small>
              </div>
            </div>
          </panel>

          <!-- Optional Fields -->
          <panel :header="t('Campos Opcionais')" :toggleable="false" style="margin-bottom: 1.5rem;">
            <div class="p-fluid">
              <!-- Descrição -->
              <div class="field" style="margin-bottom: 0;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-align-left" style="color: var(--primary-color);"></i>
                  {{ t('Descrição') }}
                  <chip :label="t('Opcional')" severity="secondary" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  data-testid="recebimento-csv-mapping-descricao"
                  elem-id="recebimentos_csv_field_mapping_descricao"
                  :itens-select="csvHeaderOptions"
                  :placeholder="t('Selecione a coluna do CSV')"
                  v-model="fieldMapping.descricao">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Coluna com histórico ou descrição do lançamento') }}
                </small>
              </div>
            </div>
          </panel>

          <!-- Default Configuration -->
          <panel :header="t('Configuração Padrão')" :toggleable="false">
            <div class="p-fluid">
              <!-- Forma de Pagamento -->
              <div class="field" style="margin-bottom: 0;">
                <label style="display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-weight: 600;">
                  <i class="pi pi-credit-card" style="color: var(--primary-color);"></i>
                  {{ t('Forma de Pagamento') }}
                  <chip :label="t('Opcional')" severity="secondary" style="height: 1.25rem; font-size: 0.75rem;" />
                </label>
                <select-list
                  data-testid="recebimento-csv-default-forma-pagamento"
                  elem-id="csv_import_default_forma_pagamento"
                  :placeholder="t('Selecione a forma de pagamento')"
                  :itens-select="formasPagamentoOptions"
                  v-model="defaultFormaPagamentoId">
                </select-list>
                <small class="text-muted" style="display: block; margin-top: 0.25rem;">
                  {{ t('Forma de pagamento padrão para os recebimentos importados') }}
                </small>
              </div>
            </div>
          </panel>

          <!-- Auto-detection info -->
          <inline-message v-if="autoDetectedColumns" severity="success" :closable="false" style="margin-top: 1rem;">
            <div>
              <strong>{{ t('Colunas detectadas automaticamente!') }}</strong>
              {{ t('Verifique se o mapeamento está correto.') }}
            </div>
          </inline-message>
        </box>
      </template>

      <!-- Step 2: Matching Preview -->
      <template #step-2>
        <box type="primary" :title="t('Correspondência Automática')">
          <!-- Instructions -->
          <inline-message severity="info" :closable="false" style="margin-bottom: 1.5rem;">
            <div>
              {{ t('O sistema encontrou as correspondências abaixo baseado em valor e data.') }}
              {{ t('Revise e ajuste conforme necessário.') }}
            </div>
          </inline-message>

          <!-- Summary Cards -->
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 0.75rem; margin-bottom: 1.5rem;">
            <!-- Exact Confidence -->
            <box style="background: linear-gradient(135deg, var(--cyan-50) 0%, var(--cyan-100) 100%); border-left: 4px solid var(--cyan-600);"
                  v-tooltip.top="t('Valor e data coincidem exatamente - praticamente sem chance de erro')">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--cyan-600); border-radius: 50%;">
                  <i class="pi pi-verified" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--cyan-700); font-weight: 500;">{{ t('EXATA') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--cyan-800);">{{ exactConfidenceCount }}</div>
                </div>
              </div>
            </box>

            <!-- High Confidence -->
            <box style="background: linear-gradient(135deg, var(--green-50) 0%, var(--green-100) 100%); border-left: 4px solid var(--green-500);">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--green-500); border-radius: 50%;">
                  <i class="pi pi-check" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--green-700); font-weight: 500;">{{ t('ALTA') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--green-800);">{{ highConfidenceCount }}</div>
                </div>
              </div>
            </box>

            <!-- Medium Confidence -->
            <box style="background: linear-gradient(135deg, var(--yellow-50) 0%, var(--yellow-100) 100%); border-left: 4px solid var(--yellow-500);">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--yellow-500); border-radius: 50%;">
                  <i class="pi pi-exclamation-triangle" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--yellow-700); font-weight: 500;">{{ t('MÉDIA CONF.') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--yellow-800);">{{ mediumConfidenceCount }}</div>
                </div>
              </div>
            </box>

            <!-- Low/None Confidence -->
            <box style="background: linear-gradient(135deg, var(--red-50) 0%, var(--red-100) 100%); border-left: 4px solid var(--red-500);">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--red-500); border-radius: 50%;">
                  <i class="pi pi-question" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--red-700); font-weight: 500;">{{ t('SEM MATCH') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--red-800);">{{ lowConfidenceCount }}</div>
                </div>
              </div>
            </box>

            <!-- Skipped (negative values) -->
            <box style="background: linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%); border-left: 4px solid var(--gray-500);"
                  v-tooltip.top="t('Valores negativos (débitos) foram filtrados automaticamente')">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--gray-500); border-radius: 50%;">
                  <i class="pi pi-minus" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--gray-700); font-weight: 500;">{{ t('NEGATIVOS') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--gray-800);">{{ skippedCount }}</div>
                </div>
              </div>
            </box>

            <!-- Already Conciliated (matched to paid compromissos) -->
            <box v-if="alreadyConciliatedCount > 0"
                  style="background: linear-gradient(135deg, var(--purple-50) 0%, var(--purple-100) 100%); border-left: 4px solid var(--purple-500);"
                  v-tooltip.top="t('Linhas já conciliadas anteriormente (encontradas em compromissos pagos)')">
              <div style="display: flex; align-items: center; gap: 0.75rem;">
                <div style="flex-shrink: 0; width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center; background: var(--purple-500); border-radius: 50%;">
                  <i class="pi pi-history" style="font-size: 1.25rem; color: white;"></i>
                </div>
                <div>
                  <div style="font-size: 0.75rem; color: var(--purple-700); font-weight: 500;">{{ t('JÁ CONCILIADOS') }}</div>
                  <div style="font-size: 1.5rem; font-weight: 700; color: var(--purple-800);">{{ alreadyConciliatedCount }}</div>
                </div>
              </div>
            </box>
          </div>

          <!-- Loading state -->
          <div v-if="isMatching" style="text-align: center; padding: 2rem;">
            <spinner style="width: 3rem; height: 3rem;" />
            <p style="margin-top: 1rem;">{{ t('Processando correspondências...') }}</p>
          </div>

          <!-- Matching Table -->
          <div v-else class="table-responsive" style="max-height: 400px; overflow-y: auto;">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th style="width: 50px;">{{ t('#') }}</th>
                  <th style="min-width: 100px;">{{ t('Data no CSV') }}</th>
                  <th style="min-width: 120px;">{{ t('Valor no CSV') }}</th>
                  <th style="min-width: 150px;">{{ t('Descrição no CSV') }}</th>
                  <th style="min-width: 250px;">{{ t('Compromisso Sugerido') }}</th>
                  <th style="width: 100px;">{{ t('Compromisso') }}</th>
                  <th style="width: 100px;">{{ t('Confiança') }}</th>
                  <th style="width: 80px; text-align: center;">{{ t('Ação') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in visibleProcessedRows" :key="row.rowIndex"
                    :class="getRowClass(row)">
                  <td>{{ index + 1 }}</td>
                  <td>{{ formatDisplayDate(row.date) }}</td>
                  <td>{{ n(parseFloat(String(row.value)) || 0, 'currency') }}</td>
                  <td class="description-cell">{{ row.description || '-' }}</td>
                  <td>
                    <template v-if="row.status === 'already_conciliated'">
                      <span class="text-muted" style="font-size: 0.85rem;">
                        <i class="pi pi-check-circle" style="color: var(--purple-500); margin-right: 0.25rem;"></i>
                        {{ row.selectedMatch?.compromisso?.cliente_nome || 'N/A' }}
                        <small style="display: block; color: var(--purple-600);">
                          {{ t('Já importado - Compromisso Pago') }}
                        </small>
                      </span>
                    </template>
                    <template v-else-if="row.matches && row.matches.length > 0">
                      <select-list
                        :data-testid="'recebimento-csv-match-' + row.rowIndex"
                        :elem-id="'csv_match_' + row.rowIndex"
                        :itens-select="getMatchOptions(row)"
                        :placeholder="t('Selecione...')"
                        v-model="row.selectedMatchId"
                        @update:modelValue="handleMatchSelection(row, $event)">
                      </select-list>
                    </template>
                    <template v-else>
                      <span class="text-danger">{{ t('Nenhuma correspondência') }}</span>
                    </template>
                  </td>
                  <td style="text-align: center;">
                    <span
                      v-if="row.selectedMatch?.compromisso?.compromisso_id"
                      class="clickable-id"
                      @click="openCompromissoModal(row.selectedMatch.compromisso.compromisso_id)">
                      {{ row.selectedMatch.compromisso.compromisso_id }}
                      <i class="pi pi-pencil"></i>
                    </span>
                    <span v-else class="text-muted">-</span>
                  </td>
                  <td style="text-align: center;">
                    <Tag v-if="row.status === 'already_conciliated'" severity="secondary" :value="t('Já Pago')" style="background: var(--purple-100); color: var(--purple-700);" />
                    <Tag v-else-if="row.confidence === 'EXACT'" severity="info" :value="t('Exata')" />
                    <Tag v-else-if="row.confidence === 'HIGH'" severity="success" :value="t('Alta')" />
                    <Tag v-else-if="row.confidence === 'MEDIUM'" severity="warn" :value="t('Média')" />
                    <Tag v-else-if="row.confidence === 'LOW'" severity="danger" :value="t('Baixa')" />
                    <Tag v-else severity="secondary" :value="t('Nenhuma')" />
                  </td>
                  <td style="text-align: center;">
                    <Button
                      v-if="row.status !== 'already_conciliated'"
                      :data-testid="'recebimento-csv-toggle-row-' + row.rowIndex"
                      :icon="row.checked ? 'pi pi-times' : 'pi pi-plus'"
                      :severity="row.checked ? 'danger' : 'success'"
                      size="small"
                      text
                      rounded
                      :disabled="!row.selectedMatch?.compromisso"
                      @click="toggleRowChecked(row)"
                      v-tooltip.top="row.checked ? t('Remover da importação') : t('Incluir na importação')">
                    </Button>
                    <span v-else class="text-muted">
                      <i class="pi pi-lock" style="color: var(--purple-400);" v-tooltip.top="t('Linha já conciliada - não pode ser alterada')"></i>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </box>
      </template>

      <!-- Step 3: Review and Confirm -->
      <template #step-3>
        <box type="primary" :title="t('Revisão Final')">
          <!-- Summary -->
          <inline-message severity="success" :closable="false" style="margin-bottom: 1.5rem;">
            <div>
              <strong>{{ rowsToImport.length }}</strong> {{ t('recebimentos serão criados totalizando') }}
              <strong>{{ n(parseFloat(String(totalValueToImport)) || 0, 'currency') }}</strong>
            </div>
          </inline-message>

          <!-- Final Review Table -->
          <div class="table-responsive" style="max-height: 350px; overflow-y: auto;">
            <table class="table table-sm table-bordered">
              <thead>
                <tr>
                  <th style="width: 50px;">{{ t('#') }}</th>
                  <th style="min-width: 100px;">{{ t('Data') }}</th>
                  <th style="min-width: 120px;">{{ t('Valor') }}</th>
                  <th style="min-width: 200px;">{{ t('Compromisso') }}</th>
                  <th style="min-width: 150px;">{{ t('Cliente') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in rowsToImport" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>{{ formatDisplayDate(row.date) }}</td>
                  <td>{{ n(parseFloat(String(row.value)) || 0, 'currency') }}</td>
                  <td>
                    #{{ row.selectedMatch?.compromisso?.compromisso_id }}
                    <small class="text-muted">
                      ({{ n(parseFloat(String(row.selectedMatch?.compromisso?.valor_restante ?? '')) || 0, 'currency') }} {{ t('pendente') }})
                    </small>
                  </td>
                  <td>{{ row.selectedMatch?.compromisso?.cliente_nome || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </box>
      </template>

      <!-- Step 4: Import Progress -->
      <template #step-4>
        <box type="primary" :title="t('Importando Recebimentos')">
          <!-- Progress Bar -->
          <div style="margin-bottom: 1.5rem;">
            <progress-bar :value="importProgress" :showValue="true" style="height: 1.5rem;" />
          </div>

          <!-- Stats Cards -->
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; margin-bottom: 1.5rem;">
            <!-- Processed -->
            <box style="background: var(--blue-50); border-left: 4px solid var(--blue-500);">
              <div style="text-align: center;">
                <div style="font-size: 0.875rem; color: var(--blue-700);">{{ t('Processados') }}</div>
                <div style="font-size: 2rem; font-weight: 700; color: var(--blue-800);">{{ importStats.processed }}</div>
              </div>
            </box>

            <!-- Success -->
            <box style="background: var(--green-50); border-left: 4px solid var(--green-500);">
              <div style="text-align: center;">
                <div style="font-size: 0.875rem; color: var(--green-700);">{{ t('Sucessos') }}</div>
                <div style="font-size: 2rem; font-weight: 700; color: var(--green-800);">{{ importStats.success }}</div>
              </div>
            </box>

            <!-- Errors -->
            <box style="background: var(--red-50); border-left: 4px solid var(--red-500);">
              <div style="text-align: center;">
                <div style="font-size: 0.875rem; color: var(--red-700);">{{ t('Erros') }}</div>
                <div style="font-size: 2rem; font-weight: 700; color: var(--red-800);">{{ importStats.errors }}</div>
              </div>
            </box>
          </div>

          <!-- Completion Message -->
          <inline-message v-if="importComplete" :severity="importStats.errors === 0 ? 'success' : 'warn'" :closable="false">
            <div>
              {{ t('Importação concluída!') }}
              {{ importStats.success }} {{ t('recebimentos criados com sucesso') }}<span v-if="importStats.errors > 0">, {{ importStats.errors }} {{ t('erros') }}</span>.
            </div>
          </inline-message>
        </box>
      </template>
    </wizard-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useRecebimento } from '../../composables/useRecebimento';
import { useFormasPagamento } from '../../composables/useFormasPagamento';
import { useDateFormatter } from '../../composables/useDateFormatter';
import WizardModal from '../../ui/WizardModal.vue';

type Id = number | string;

interface SelectOption {
  ID: string;
  Name: string;
}

interface FieldMapping {
  valor: string;
  data: string;
  descricao: string;
}

interface FormaPagamentoApi {
  forma_pagamento_id?: number;
  forma_pagamento_nome?: string;
  ID?: string;
  Name?: string;
}

interface CompromissoData {
  compromisso_id?: number;
  compromisso_data?: string;
  compromisso_valor_total?: number | string;
  valor_restante?: number | string;
  cliente_nome?: string;
  [key: string]: unknown;
}

interface MatchEntry {
  compromisso: CompromissoData;
  score: number;
  confidence: 'EXACT' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
}

interface ProcessedRow {
  rowIndex: number;
  date: string;
  value: number;
  isPositive: boolean;
  description: string;
  originalValue: string;
  matches?: MatchEntry[];
  selectedMatch?: MatchEntry | null;
  selectedMatchId?: string;
  confidence: 'EXACT' | 'HIGH' | 'MEDIUM' | 'LOW' | 'NONE';
  status?: 'skipped' | 'already_conciliated' | string;
  excluded?: boolean;
  checked: boolean;
}

interface CsvRow {
  value: number;
  isPositive: boolean;
  date: string;
  description: string;
  originalValue: string;
}

interface ImportResults {
  success: unknown[];
  errors: unknown[];
}

interface WizardStep {
  title: string;
  description: string;
  validate: () => boolean;
}

interface StepChangeEvent {
  to: number;
  from?: number;
}

// Get i18n composable
const { t, n } = useI18n();

// Get date formatter (locale-aware)
// fromApiDateToDate: parses YYYY-MM-DD string to Date object (noon local, no TZ shift)
const { formatDate, fromApiDateToDate } = useDateFormatter();

// Get recebimento composable
const {
  parseCsvValue,
  parseCsvDate,
  processAllCsvRows,
  createRecebimentosFromCsv,
  fetchCompromissosWithPaymentStatus
} = useRecebimento();

// Get formas de pagamento composable
const { listarFormasPagamento } = useFormasPagamento();

// Define emits
const emit = defineEmits<{
  'close': [];
}>();

// Reactive data
const show = ref<boolean>(false);
const csvFile = ref<File | null>(null);
const csvContent = ref<string>('');
const csvHeaders = ref<string[]>([]);
const csvPreview = ref<string[][]>([]);
const uploadProgress = ref<number>(0);

const fieldMapping = reactive<FieldMapping>({
  valor: '',
  data: '',
  descricao: ''
});

const autoDetectedColumns = ref<boolean>(false);

const defaultFormaPagamentoId = ref<string>('');

const formasPagamentoOptions = ref<SelectOption[]>([]);

// Matching state
const compromissos = ref<CompromissoData[]>([]);
const processedRows = ref<ProcessedRow[]>([]);
const isMatching = ref<boolean>(false);

// Import state
const importProgress = ref<number>(0);
const importStats = reactive({
  processed: 0,
  success: 0,
  errors: 0
});
const importComplete = ref<boolean>(false);

// Wizard steps configuration
const wizardSteps = computed<WizardStep[]>(() => [
  {
    title: t('Upload'),
    description: t('Selecione o arquivo CSV'),
    validate: () => csvPreview.value.length > 0
  },
  {
    title: t('Mapeamento'),
    description: t('Configure os campos'),
    validate: () => canProceedToMatching.value
  },
  {
    title: t('Correspondência'),
    description: t('Revise as correspondências'),
    validate: () => rowsToImport.value.length > 0
  },
  {
    title: t('Confirmação'),
    description: t('Confirme a importação'),
    validate: () => rowsToImport.value.length > 0
  },
  {
    title: t('Importação'),
    description: t('Processando'),
    validate: () => true
  }
]);

// Computed properties
const csvHeaderOptions = computed<SelectOption[]>(() => {
  return [
    { ID: '', Name: t('Selecionar') },
    ...csvHeaders.value.map((header: string, index: number): SelectOption => ({
      ID: index.toString(),
      Name: header
    }))
  ];
});

const canProceedToMatching = computed<boolean>(() => {
  // Only require valor and data mapping - franquia is optional since we match against all compromissos
  return fieldMapping.valor !== '' &&
         fieldMapping.data !== '';
});

// Matching stats - exclude skipped and already_conciliated rows
const exactConfidenceCount = computed<number>(() => {
  return processedRows.value.filter(r => r.confidence === 'EXACT' && !r.excluded && r.status !== 'skipped' && r.status !== 'already_conciliated').length;
});

const highConfidenceCount = computed<number>(() => {
  return processedRows.value.filter(r => r.confidence === 'HIGH' && !r.excluded && r.status !== 'skipped' && r.status !== 'already_conciliated').length;
});

const mediumConfidenceCount = computed<number>(() => {
  return processedRows.value.filter(r => r.confidence === 'MEDIUM' && !r.excluded && r.status !== 'skipped' && r.status !== 'already_conciliated').length;
});

const lowConfidenceCount = computed<number>(() => {
  return processedRows.value.filter(r => (r.confidence === 'LOW' || r.confidence === 'NONE') && !r.excluded && r.status !== 'skipped' && r.status !== 'already_conciliated').length;
});

const skippedCount = computed<number>(() => {
  return processedRows.value.filter(r => r.status === 'skipped').length;
});

const alreadyConciliatedCount = computed<number>(() => {
  return processedRows.value.filter(r => r.status === 'already_conciliated').length;
});

// Confidence level priority for sorting (higher = better)
const confidencePriority: Record<string, number> = {
  'EXACT': 5,
  'HIGH': 4,
  'MEDIUM': 3,
  'LOW': 2,
  'NONE': 1
};

// Visible rows (excluding negative/skipped transactions), sorted by confidence desc
const visibleProcessedRows = computed<ProcessedRow[]>(() => {
  return processedRows.value
    .filter(r => r.status !== 'skipped')
    .sort((a, b) => {
      // Sort by confidence level first (descending)
      const confDiff = (confidencePriority[b.confidence] || 0) - (confidencePriority[a.confidence] || 0);
      if (confDiff !== 0) return confDiff;
      // Then by score (descending)
      return (b.selectedMatch?.score || 0) - (a.selectedMatch?.score || 0);
    });
});

// Rows ready for import - only checked rows with valid matches
const rowsToImport = computed<ProcessedRow[]>(() => {
  return processedRows.value.filter(r =>
    r.status !== 'skipped' &&
    r.status !== 'already_conciliated' &&
    r.checked &&
    !!r.selectedMatch?.compromisso
  );
});

const totalValueToImport = computed<number>(() => {
  return rowsToImport.value.reduce((sum: number, r: ProcessedRow) => sum + (r.value || 0), 0);
});

// Wizard event handlers
const handleStepChange = async (event: StepChangeEvent): Promise<void> => {
  log('RecebimentosCsvImportModal', 'handleStepChange', 'Step changed', event);

  if (event.to === 2) {
    // Entering matching step - process CSV and find matches
    await processAndMatch();
  } else if (event.to === 4) {
    // Entering import step - start import
    await startImport();
  }
};

const handleFinish = (): void => {
  log('RecebimentosCsvImportModal', 'handleFinish', 'Wizard finished');
  closeModal();
};

// Methods
const openModal = (): void => {
  log('RecebimentosCsvImportModal', 'openModal', 'Open');
  resetForm();
  show.value = true;
  loadOptions();
};

const closeModal = (): void => {
  log('RecebimentosCsvImportModal', 'closeModal', 'Close');
  show.value = false;
  resetForm();

  if (importStats.success > 0) {
    eventBus.emit('recebimento.csv.imported');
  }

  emit('close');
};

const resetForm = (): void => {
  log('RecebimentosCsvImportModal', 'resetForm', 'Reset form');
  csvFile.value = null;
  csvContent.value = '';
  csvHeaders.value = [];
  csvPreview.value = [];
  uploadProgress.value = 0;
  Object.assign(fieldMapping, { valor: '', data: '', descricao: '' });
  autoDetectedColumns.value = false;
  processedRows.value = [];
  compromissos.value = [];
  importProgress.value = 0;
  Object.assign(importStats, { processed: 0, success: 0, errors: 0 });
  importComplete.value = false;
};

const handleFileUpload = (file: File | null): void => {
  log('RecebimentosCsvImportModal', 'handleFileUpload', 'File present?', !!file);
  if (!file) return;

  csvFile.value = file;
  uploadProgress.value = 0;

  const reader = new FileReader();
  reader.onload = (e: ProgressEvent<FileReader>) => {
    csvContent.value = String(e.target?.result ?? '');
    parseCSV();
  };

  reader.onprogress = (e: ProgressEvent<FileReader>) => {
    if (e.lengthComputable) {
      uploadProgress.value = Math.round((e.loaded / e.total) * 100);
    }
  };

  reader.readAsText(file);
};

const parseCSV = (): void => {
  log('RecebimentosCsvImportModal', 'parseCSV', 'Begin');
  const lines = csvContent.value.split('\n').filter(line => line.trim());
  if (lines.length === 0) return;

  // Detect separator
  const firstLine = lines[0];
  const separator = firstLine.includes(';') ? ';' : ',';

  // Parse headers
  csvHeaders.value = firstLine.split(separator).map(header => header.trim().replace(/"/g, ''));

  // Parse preview data (all rows)
  csvPreview.value = lines.slice(1).map(line =>
    line.split(separator).map(cell => cell.trim().replace(/"/g, ''))
  );

  uploadProgress.value = 100;

  // Auto-detect column mappings
  autoDetectColumnMappings();
};

/**
 * Auto-detect column mappings based on header names and data patterns
 */
const autoDetectColumnMappings = (): void => {
  log('RecebimentosCsvImportModal', 'autoDetectColumnMappings', 'Begin', csvHeaders.value);

  // Reset auto-detection flag
  autoDetectedColumns.value = false;

  // Patterns for detecting columns (case-insensitive)
  const datePatterns = [
    /^data$/i, /^date$/i, /^dt$/i, /^dt_/i, /data_/i,
    /vencimento/i, /lancamento/i, /lançamento/i, /movimento/i,
    /^when$/i, /^dia$/i, /^mes$/i
  ];

  const valuePatterns = [
    /^valor$/i, /^value$/i, /^val$/i, /valor_/i,
    /montante/i, /quantia/i, /saldo/i, /total/i,
    /credito/i, /crédito/i, /debito/i, /débito/i,
    /amount/i, /^vlr$/i, /price/i, /preco/i, /preço/i
  ];

  const descriptionPatterns = [
    /^descricao$/i, /^descrição$/i, /^desc$/i, /^description$/i,
    /historico/i, /histórico/i, /hist/i,
    /memo/i, /observacao/i, /observação/i, /^obs$/i,
    /detalhe/i, /detail/i, /^nome$/i, /name/i,
    /favorecido/i, /beneficiario/i, /beneficiário/i
  ];

  let detectedDate: string | null = null;
  let detectedValue: string | null = null;
  let detectedDescription: string | null = null;

  // First pass: match by header name patterns
  csvHeaders.value.forEach((header, index) => {
    const headerNormalized = header.toLowerCase().trim();
    const indexStr = index.toString();

    // Check for date column
    if (!detectedDate) {
      for (const pattern of datePatterns) {
        if (pattern.test(headerNormalized)) {
          detectedDate = indexStr;
          log('RecebimentosCsvImportModal', 'autoDetect', 'Found date column by name', { header, index });
          break;
        }
      }
    }

    // Check for value column
    if (!detectedValue) {
      for (const pattern of valuePatterns) {
        if (pattern.test(headerNormalized)) {
          detectedValue = indexStr;
          log('RecebimentosCsvImportModal', 'autoDetect', 'Found value column by name', { header, index });
          break;
        }
      }
    }

    // Check for description column
    if (!detectedDescription) {
      for (const pattern of descriptionPatterns) {
        if (pattern.test(headerNormalized)) {
          detectedDescription = indexStr;
          log('RecebimentosCsvImportModal', 'autoDetect', 'Found description column by name', { header, index });
          break;
        }
      }
    }
  });

  // Second pass: if not found by name, try to detect by data patterns
  if (csvPreview.value.length > 0 && (!detectedDate || !detectedValue)) {
    const sampleRows = csvPreview.value.slice(0, 5); // Check first 5 rows

    csvHeaders.value.forEach((header, index) => {
      if (detectedDate && detectedValue) return; // Already found both

      const indexStr = index.toString();
      const sampleValues = sampleRows.map(row => row[index]).filter(v => v);

      // Try to detect date by data format (DD/MM/YYYY, YYYY-MM-DD, etc)
      if (!detectedDate) {
        const dateRegex = /^(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2})$/;
        const dateMatches = sampleValues.filter(v => dateRegex.test(v.trim()));
        if (dateMatches.length >= Math.min(3, sampleValues.length)) {
          detectedDate = indexStr;
          log('RecebimentosCsvImportModal', 'autoDetect', 'Found date column by data pattern', { header, index });
        }
      }

      // Try to detect value by data format (numbers with comma/period decimals)
      if (!detectedValue) {
        const valueRegex = /^-?\s*[R$]?\s*[\d.,]+\s*$/;
        const valueMatches = sampleValues.filter(v => valueRegex.test(v.trim()));
        if (valueMatches.length >= Math.min(3, sampleValues.length)) {
          // Additional check: parse as number
          const parsed = sampleValues.map(v => {
            const cleaned = v.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
            return parseFloat(cleaned);
          });
          const validNumbers = parsed.filter(n => !isNaN(n) && n !== 0);
          if (validNumbers.length >= Math.min(2, sampleValues.length)) {
            detectedValue = indexStr;
            log('RecebimentosCsvImportModal', 'autoDetect', 'Found value column by data pattern', { header, index });
          }
        }
      }
    });
  }

  // Apply detected mappings
  if (detectedDate) {
    fieldMapping.data = detectedDate;
  }
  if (detectedValue) {
    fieldMapping.valor = detectedValue;
  }
  if (detectedDescription) {
    fieldMapping.descricao = detectedDescription;
  }

  // Set flag if at least required fields were detected
  autoDetectedColumns.value = !!(detectedDate && detectedValue);

  log('RecebimentosCsvImportModal', 'autoDetectColumnMappings', 'Result', {
    date: detectedDate,
    value: detectedValue,
    description: detectedDescription,
    autoDetected: autoDetectedColumns.value
  });
};

const loadOptions = async (): Promise<void> => {
  log('RecebimentosCsvImportModal', 'loadOptions', 'Begin');
  try {
    // Load formas de pagamento
    const formasData = await listarFormasPagamento() as FormaPagamentoApi[] | null;
    if (formasData) {
      formasPagamentoOptions.value = formasData.map((fp: FormaPagamentoApi): SelectOption => ({
        ID: fp.forma_pagamento_id?.toString() || fp.ID || '',
        Name: fp.forma_pagamento_nome || fp.Name || ''
      }));
      log('RecebimentosCsvImportModal', 'loadOptions', 'Loaded formas de pagamento', formasPagamentoOptions.value.length);
    }

  } catch (error) {
    console.error('Error loading options:', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar opções: ') + message);
  }
};

const processAndMatch = async (): Promise<void> => {
  log('RecebimentosCsvImportModal', 'processAndMatch', 'Begin');
  isMatching.value = true;

  try {
    // 1. Load compromissos for matching
    compromissos.value = await fetchCompromissosWithPaymentStatus() as CompromissoData[];
    log('RecebimentosCsvImportModal', 'processAndMatch', 'Loaded compromissos', compromissos.value.length);

    // 2. Parse CSV rows with mapped fields
    const lines = csvContent.value.split('\n').filter(line => line.trim());
    const separator = lines[0].includes(';') ? ';' : ',';

    const csvRows: CsvRow[] = lines.slice(1).map((line: string): CsvRow => {
      const cells = line.split(separator).map(cell => cell.trim().replace(/"/g, ''));

      const valorStr = cells[parseInt(fieldMapping.valor)] || '';
      const { value, isPositive } = parseCsvValue(valorStr) as { value: number; isPositive: boolean };
      const date = parseCsvDate(cells[parseInt(fieldMapping.data)] || '') as string;
      const description = fieldMapping.descricao !== '' ? cells[parseInt(fieldMapping.descricao)] : '';

      return {
        value,
        isPositive,
        date,
        description,
        originalValue: valorStr
      };
    });

    log('RecebimentosCsvImportModal', 'processAndMatch', 'Parsed CSV rows', csvRows.length);

    // 3. Process all rows and find matches
    processedRows.value = processAllCsvRows(csvRows, compromissos.value) as ProcessedRow[];

    // 4. Set selectedMatchId for dropdown binding and auto-check based on confidence
    processedRows.value.forEach((row: ProcessedRow) => {
      if (row.selectedMatch) {
        row.selectedMatchId = row.selectedMatch.compromisso?.compromisso_id?.toString() || '';
      } else {
        row.selectedMatchId = '';
      }
      // Auto-check EXACT and HIGH confidence matches, others need manual selection
      if (row.status === 'already_conciliated') {
        row.checked = false; // Already conciliated rows cannot be imported
      } else if (row.confidence === 'EXACT' || row.confidence === 'HIGH') {
        row.checked = true; // Auto-select high confidence matches
      } else {
        row.checked = false; // MEDIUM, LOW, NONE need manual selection
      }
    });

    log('RecebimentosCsvImportModal', 'processAndMatch', 'Processed rows', processedRows.value.length);

  } catch (error) {
    console.error('Error processing matches:', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao processar correspondências: ') + message);
  } finally {
    isMatching.value = false;
  }
};

const getMatchOptions = (row: ProcessedRow): SelectOption[] => {
  if (!row.matches || row.matches.length === 0) {
    return [{ ID: '', Name: t('Nenhuma correspondência') }];
  }

  return row.matches.map((match: MatchEntry): SelectOption => ({
    ID: String(match.compromisso.compromisso_id),
    Name: `${match.compromisso.cliente_nome || 'N/A'} - ${n(parseFloat(String(match.compromisso.compromisso_valor_total ?? match.compromisso.valor_restante ?? '')) || 0, 'currency')} - ${formatDisplayDate(match.compromisso.compromisso_data)}`
  }));
};

const handleMatchSelection = (row: ProcessedRow, selectedId: string): void => {
  log('RecebimentosCsvImportModal', 'handleMatchSelection', 'Selection', { rowIndex: row.rowIndex, selectedId });

  if (!selectedId) {
    row.selectedMatch = null;
    row.confidence = 'NONE';
    return;
  }

  const match = row.matches?.find((m: MatchEntry) => String(m.compromisso.compromisso_id) === selectedId);
  if (match) {
    row.selectedMatch = match;
    row.confidence = match.confidence;
  }
};

const toggleRowChecked = (row: ProcessedRow): void => {
  row.checked = !row.checked;
  log('RecebimentosCsvImportModal', 'toggleRowChecked', 'Toggled', { rowIndex: row.rowIndex, checked: row.checked });
};

const openCompromissoModal = (compromissoId: number | undefined): void => {
  if (!compromissoId) return;
  log('RecebimentosCsvImportModal', 'openCompromissoModal', 'Opening compromisso modal', compromissoId);
  eventBus.emit('gestao-compromisso.open', { compromissoId });
};

const getRowClass = (row: ProcessedRow): string => {
  if (row.status === 'already_conciliated') return 'row-already-conciliated';
  if (!row.checked) return 'row-unchecked';
  if (row.confidence === 'EXACT') return 'row-exact-confidence';
  if (row.confidence === 'HIGH') return 'row-high-confidence';
  if (row.confidence === 'MEDIUM') return 'row-medium-confidence';
  return '';
};

const startImport = async (): Promise<void> => {
  log('RecebimentosCsvImportModal', 'startImport', 'Begin');

  Object.assign(importStats, { processed: 0, success: 0, errors: 0 });
  importProgress.value = 0;
  importComplete.value = false;

  const rows = rowsToImport.value;
  const total = rows.length;

  if (total === 0) {
    eventBus.emit('globalMessage.warn', t('Nenhum recebimento para importar'));
    return;
  }

  try {
    const results = await createRecebimentosFromCsv(rows, {
      formaPagamentoId: defaultFormaPagamentoId.value || null
    }) as ImportResults;

    importStats.success = results.success.length;
    importStats.errors = results.errors.length;
    importStats.processed = results.success.length + results.errors.length;
    importProgress.value = 100;
    importComplete.value = true;

    if (results.success.length > 0) {
      eventBus.emit('globalMessage.success',
        `${results.success.length} ${t('recebimentos criados com sucesso')}`
      );
    }

    if (results.errors.length > 0) {
      eventBus.emit('globalMessage.warn',
        `${results.errors.length} ${t('erros durante a importação')}`
      );
    }

  } catch (error) {
    console.error('Error during import:', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro durante a importação: ') + message);
    importComplete.value = true;
  }
};

const formatDisplayDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '-';
  // Extract just the date part (remove time if present)
  const dateOnly = dateStr.split(' ')[0];
  // Use locale-aware date formatting (fromApiDateToDate for date-only strings)
  const dateObj = fromApiDateToDate(dateOnly);
  if (dateObj) {
    return formatDate(dateObj);
  }
  return dateStr;
};

// Standard modal open handler (for modal.recebimentos-csv-import.open event)
const handleModalOpen = (data?: unknown): void => {
  log('RecebimentosCsvImportModal', 'handleModalOpen', 'Standard modal open event received', data);
  openModal();
};

const handlePermissionsReady = (): void => {
  log('RecebimentosCsvImportModal', 'event:permissions.ready', 'Received');
  loadOptions();
};

// Lifecycle hooks
onMounted(() => {
  log('RecebimentosCsvImportModal', 'mounted', 'Component mounted');
  loadOptions();

  // Standard modal events (preferred pattern)
  eventBus.on('modal.recebimentos-csv-import.open', handleModalOpen);
  eventBus.on('modal.recebimentos-csv-import.close', closeModal);

  eventBus.on('permissions.ready', handlePermissionsReady);
});

onBeforeUnmount(() => {
  // Remove standard modal events
  eventBus.off('modal.recebimentos-csv-import.open', handleModalOpen);
  eventBus.off('modal.recebimentos-csv-import.close', closeModal);
  eventBus.off('permissions.ready', handlePermissionsReady);
});

// Expose public methods for parent components
defineExpose({
  openModal,
  closeModal
});
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.row-unchecked {
  background-color: var(--surface-100) !important;
  opacity: 0.5;
}

.row-unchecked td {
  color: var(--text-color-secondary) !important;
}

.row-already-conciliated {
  background-color: var(--purple-50) !important;
  opacity: 0.7;
}

.row-already-conciliated td {
  color: var(--purple-700) !important;
}

/* Checked rows - full opacity with confidence-based colors */
.row-exact-confidence {
  background-color: var(--cyan-100) !important;
  opacity: 1 !important;
}

.row-high-confidence {
  background-color: var(--green-100) !important;
  opacity: 1 !important;
}

.row-medium-confidence {
  background-color: var(--yellow-100) !important;
  opacity: 1 !important;
}

.text-muted {
  color: var(--text-color-secondary);
}

.text-danger {
  color: var(--red-500);
}

/* Table styling */
.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.5rem;
  border: 1px solid var(--surface-border);
  vertical-align: middle;
}

.table thead {
  background: var(--surface-100);
}

.table thead th {
  background: var(--surface-100);
  font-weight: 600;
}

/* Sticky header fix - ensure solid background */
.table-responsive .table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #f8f9fa;
  background-color: var(--surface-100, #f8f9fa);
}

.table-responsive .table thead th {
  background-color: #f8f9fa;
  background-color: var(--surface-100, #f8f9fa);
}

.table-bordered {
  border: 1px solid var(--surface-border);
}

.table-sm th,
.table-sm td {
  padding: 0.25rem 0.5rem;
}

/* Description cell - truncate long text */
.description-cell {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

/* Clickable ID */
.clickable-id {
  color: #3b82f6 !important;
  cursor: pointer;
  font-weight: 600;
  transition: color 0.2s;
}

.clickable-id:hover {
  color: #1d4ed8 !important;
  text-decoration: underline;
}

/* Smaller select in matching table */
:deep(.table .p-autocomplete) {
  font-size: 0.7rem !important;
}

:deep(.table .p-autocomplete-input) {
  font-size: 0.7rem !important;
  padding: 0.25rem 0.4rem !important;
  height: 1.75rem !important;
}

:deep(.table .p-autocomplete-dropdown) {
  width: 1.75rem !important;
  padding: 0 !important;
}
</style>
